/**
 * RSI(2) sub-panel rendered below `Rsi2CandleChart`, sharing the exact same
 * x-axis category labels (trade dates) so the two charts line up visually.
 * Plots the RSI(2) series (0-100), a horizontal dashed line at y=10 (this
 * strategy's oversold threshold -- NOT the generic RSI(14) "30" threshold;
 * see `app.services.rsi2_pullback.scoring.DEFAULT_RSI_THRESHOLD`), and the
 * same Signal-Date vertical marker used on the candlestick chart above.
 */
import { useMemo, useRef } from "react";
import { Box } from "@mui/material";
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip as ChartTooltip,
  type Chart as ChartInstance,
} from "chart.js";
import { Line } from "react-chartjs-2";
import {
  buildVerticalMarkerPlugin,
  COLOR_RSI_LINE,
  COLOR_RSI_OVERSOLD,
  COLOR_SIGNAL_MARKER,
  formatDateDDMMYY,
  type VerticalMarker,
} from "./chartPluginsAndColors";

ChartJS.register(LinearScale, CategoryScale, PointElement, LineElement, ChartTooltip, Legend);

const RSI_MARKER_PLUGIN_ID = "rsi2RsiVerticalMarkers";
const rsiVerticalMarkerPlugin = buildVerticalMarkerPlugin(RSI_MARKER_PLUGIN_ID);

const RSI_OVERSOLD_THRESHOLD = 10;

function toNum(value: string | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const n = Number.parseFloat(value);
  return Number.isNaN(n) ? null : n;
}

export interface Rsi2RsiPanelProps {
  labels: string[];
  rsi2: (string | null)[];
  signalDate: string;
}

export function Rsi2RsiPanel({ labels, rsi2, signalDate }: Rsi2RsiPanelProps) {
  /* eslint-disable @typescript-eslint/no-explicit-any -- same Chart.js
   * scriptable-option typed-`any` convention as PayoffChart.tsx / Rsi2CandleChart.tsx. */
  const chartRef = useRef<ChartInstance<"line"> | null>(null);

  const signalIndex = labels.indexOf(signalDate);
  const markers: VerticalMarker[] = useMemo(
    () => (signalIndex >= 0 ? [{ index: signalIndex, color: COLOR_SIGNAL_MARKER, label: "Setup" }] : []),
    [signalIndex]
  );

  const data: any = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: "RSI(2)",
          data: rsi2.map(toNum),
          borderColor: COLOR_RSI_LINE,
          borderWidth: 1.5,
          pointRadius: 0,
          spanGaps: false,
        },
        {
          label: "Oversold 10",
          data: labels.map(() => RSI_OVERSOLD_THRESHOLD),
          borderColor: COLOR_RSI_OVERSOLD,
          borderDash: [4, 3],
          borderWidth: 1,
          pointRadius: 0,
        },
      ],
    }),
    [labels, rsi2]
  );

  const options: any = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { position: "bottom", labels: { usePointStyle: true, boxWidth: 8, font: { size: 10 } } },
        tooltip: {
          callbacks: {
            label: (item: any) => {
              const v = item.parsed.y;
              return v == null ? "" : `${item.dataset.label}: ${v.toFixed(2)}`;
            },
          },
        },
        [RSI_MARKER_PLUGIN_ID]: { markers },
      },
      scales: {
        x: {
          type: "category",
          ticks: {
            maxRotation: 45,
            minRotation: 0,
            autoSkip: true,
            maxTicksLimit: 12,
            font: { size: 10 },
            // Same dd/mm/yy formatting as `Rsi2CandleChart`'s x-axis so the
            // two stacked charts' date labels stay visually consistent.
            callback(this: { getLabelForValue: (value: number) => string }, value: number) {
              return formatDateDDMMYY(this.getLabelForValue(value));
            },
          },
        },
        y: {
          min: 0,
          max: 100,
          ticks: {
            stepSize: 20,
            font: { size: 10 },
            callback: (value: number | string) => Math.round(Number(value)),
          },
        },
      },
    }),
    [markers]
  );
  /* eslint-enable @typescript-eslint/no-explicit-any */

  return (
    <Box sx={{ height: 140 }}>
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <Line ref={chartRef as any} data={data} options={options} plugins={[rsiVerticalMarkerPlugin]} />
    </Box>
  );
}

export default Rsi2RsiPanel;
